import React, { useEffect, useState } from 'react'
import { Languages, Loader2, AlertTriangle, Copy, Check, ArrowRight, History as HistoryIcon, Trash2 } from 'lucide-react'
import { useProjects } from '../hooks/useRag'
import { useTranslate, useTranslateProviders, useTranslationHistory, useDeleteTranslation } from '../hooks/useResearch'
import { TARGET_LANGS } from './TranslateButton'
import Markdown from './Markdown'

const DOMAINS = ['general', 'technical', 'conversational', 'legal', 'medical', 'ecommerce', 'financial', 'marketing']
const ctrlCls =
  'h-9 rounded-lg px-2 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist'

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }
  catch { return iso }
}

// Standalone quick-translate utility (paste text → translated, rendered as
// Markdown). Source language is auto-detected; domain tunes terminology. When a
// project is selected, results are saved to its translation history (shown below).
export default function TranslatePanel() {
  const projectsQuery = useProjects()
  const providersQuery = useTranslateProviders()
  const translate = useTranslate()
  const delTranslation = useDeleteTranslation()
  const [project, setProject] = useState('')
  const [provider, setProvider] = useState('')
  const [text, setText] = useState('')
  const [target, setTarget] = useState('en')
  const [domain, setDomain] = useState('general')
  const [copied, setCopied] = useState(false)
  const [viewResult, setViewResult] = useState(null) // a loaded history entry's translation
  const [viewingId, setViewingId] = useState(null)

  useEffect(() => {
    if (!project && projectsQuery.data?.current_project) setProject(projectsQuery.data.current_project)
  }, [project, projectsQuery.data])

  // Adopt the saved default provider once providers load (light unless t8r's
  // backend is wired and set as the preference).
  const providers = providersQuery.data?.providers ?? []
  useEffect(() => {
    if (!provider && providersQuery.data?.default) setProvider(providersQuery.data.default)
  }, [provider, providersQuery.data])

  const history = useTranslationHistory(project)
  const projects = projectsQuery.data?.projects ?? []
  const entries = history.data?.translations ?? []
  const result = viewResult ?? translate.data?.translated

  const run = () => {
    if (!text.trim()) return
    setCopied(false)
    setViewResult(null)
    setViewingId(null)
    translate.mutate({ provider: provider || 'light', text, target_lang: target, domain, project, save: !!project })
  }
  const copy = () => navigator.clipboard?.writeText(result || '').then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
  const loadEntry = (e) => {
    setText(e.source_text || '')
    setTarget(e.target_lang || 'en')
    setDomain(e.domain || 'general')
    setViewResult(e.translated || '')
    setViewingId(e.id)
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-soft dark:text-mist/60">
        Translate any text — a quote, a snippet, a draft. Source language is detected automatically.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <select value={project} onChange={(e) => setProject(e.target.value)} className={ctrlCls} title="Save translations to this project's history">
          <option value="">No project (don’t save)</option>
          {projects.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
        <label className="text-xs text-ink-soft dark:text-mist/50 flex items-center gap-1.5">
          Via
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className={ctrlCls}
            title="light = LLM, always available (no Docker); t8r = translation backend"
          >
            {providers.map((p) => (
              <option key={p.id} value={p.id} disabled={!p.available}>
                {p.label}{p.available ? '' : ' (offline)'}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-ink-soft dark:text-mist/50 flex items-center gap-1.5">
          To
          <select value={target} onChange={(e) => setTarget(e.target.value)} className={ctrlCls}>
            {TARGET_LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
        </label>
        <label className="text-xs text-ink-soft dark:text-mist/50 flex items-center gap-1.5">
          Domain
          <select value={domain} onChange={(e) => setDomain(e.target.value)} className={ctrlCls}>
            {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <button
          onClick={run}
          disabled={translate.isPending || !text.trim()}
          className="inline-flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium bg-gradient-to-r from-gold to-spark text-midnight-900 hover:opacity-90 disabled:opacity-40 transition"
        >
          {translate.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
          Translate
        </button>
      </div>

      {translate.isSuccess && translate.data?.fell_back && (
        <div className="flex items-center gap-2 text-xs rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-700 dark:border-amber-700/40 dark:bg-amber-900/15 dark:text-amber-300">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          {translate.data.fallback_reason || 'T8R unavailable — translated with Light.'}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3 items-stretch">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste text to translate…"
          className="min-h-[260px] rounded-xl p-4 text-[15px] leading-relaxed bg-white border border-parchment-border text-ink placeholder:text-ink-soft/50 focus:outline-none focus:ring-2 focus:ring-gold/40 dark:bg-midnight-800 dark:border-midnight-600 dark:text-mist"
        />
        <div className="relative min-h-[260px] rounded-xl p-4 pr-10 border border-parchment-border bg-white/60 dark:border-midnight-600 dark:bg-midnight-800/50">
          {translate.isPending ? (
            <span className="inline-flex items-center gap-2 text-ink-soft dark:text-mist/60"><Loader2 className="w-4 h-4 animate-spin" /> Translating…</span>
          ) : translate.isError ? (
            <span className="inline-flex items-center gap-1 text-sm text-red-600"><AlertTriangle className="w-4 h-4" /> {translate.error?.message}</span>
          ) : result ? (
            <>
              <Markdown>{result}</Markdown>
              <button onClick={copy} className="absolute top-3 right-3 p-1 rounded text-ink-soft hover:text-ink dark:text-mist/50" aria-label="Copy">
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </>
          ) : (
            <span className="text-ink-soft/60 dark:text-mist/40 inline-flex items-center gap-1.5"><ArrowRight className="w-4 h-4" /> Translation appears here.</span>
          )}
        </div>
      </div>

      {/* Saved translations (per project) */}
      {project && entries.length > 0 && (
        <div className="rounded-xl border border-parchment-border bg-white/60 p-4 dark:border-midnight-600 dark:bg-midnight-800/50">
          <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold flex items-center gap-1.5 mb-2">
            <HistoryIcon className="w-3.5 h-3.5" /> Saved translations · {project}
          </h3>
          <ul className="space-y-1.5">
            {entries.map((e) => (
              <li
                key={e.id}
                onClick={() => loadEntry(e)}
                className={`group flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer transition ${
                  viewingId === e.id ? 'border-gold bg-gold/5' : 'border-parchment-border hover:border-gold dark:border-midnight-600'
                }`}
              >
                <span className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-parchment-200 text-ink-soft dark:bg-midnight-700 dark:text-mist/60">
                  {e.source_lang} → {e.target_lang}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink dark:text-mist truncate">{(e.source_text || '').replace(/\s+/g, ' ').trim() || '—'}</div>
                  <div className="text-xs text-ink-soft dark:text-mist/50">{fmtTime(e.timestamp)}{e.domain && e.domain !== 'general' ? ` · ${e.domain}` : ''}</div>
                </div>
                <button
                  onClick={(ev) => { ev.stopPropagation(); delTranslation.mutate({ project, id: e.id }); if (viewingId === e.id) { setViewResult(null); setViewingId(null) } }}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded text-ink-soft hover:text-red-600 dark:text-mist/50"
                  aria-label="Delete translation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
